import React from 'react';
import { Calendar, Clock, Users } from 'lucide-react';

export function ReservationSummary({
  selectedTables,
  selectedDate,
  selectedSlot,
  duration,
  partySize,
  totalCapacity,
  error,
  success,
  loading,
  onConfirm,
  t,
  lang
}) {
  const isValid = selectedTables.length > 0 && selectedDate && selectedSlot && totalCapacity >= partySize;

  return (
    <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-6">
      <h3 className="text-[#F5F0E6] font-bold mb-4">
        {t('reservationsShared.summary')}
      </h3>
      
      <div className="space-y-3 text-sm">
        <SummaryRow 
          label={t('reservationsShared.tables')} 
          value={selectedTables.length > 0 ? selectedTables.map(t => `#${t}`).join(', ') : '-'} 
        />
        <SummaryRow 
          label={t('reservationsShared.date')} 
          value={selectedDate || '-'} 
        />
        <SummaryRow 
          label={t('reservations.select_slot')} 
          value={selectedSlot || '-'} 
        />
        <SummaryRow 
          label={t('reservations.duration')} 
          value={`${duration}h`} 
        />
        <SummaryRow 
          label={t('reservations.party_size')} 
          value={partySize} 
        />
        <SummaryRow 
          label={t('reservationsShared.capacity')} 
          value={totalCapacity}
          color={totalCapacity >= partySize ? 'text-[#2D8B70]' : 'text-[#C45B4A]'}
        />
      </div>
      
      {error && (
        <p className="text-[#C45B4A] text-sm mt-4 bg-[#C45B4A]/10 px-3 py-2 rounded-lg">{error}</p>
      )}
      {success && (
        <p className="text-[#2D8B70] text-sm mt-4 bg-[#2D8B70]/10 px-3 py-2 rounded-lg">{success}</p>
      )}
      
      <button 
        onClick={onConfirm} 
        disabled={loading || !isValid}
        className="w-full btn-primary py-3 mt-4 disabled:opacity-40 text-sm"
        data-testid="confirm-table-btn"
      >
        {loading ? '...' : t('reservations.confirm')}
      </button>
    </div>
  );
}

function SummaryRow({ label, value, color = 'text-[#F5F0E6]' }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#A8B5AE]">{label}</span>
      <span className={color}>{value}</span>
    </div>
  );
}

export function MyReservationsList({
  reservations,
  onCancel,
  t,
  lang
}) {
  const confirmedReservations = reservations.filter(r => r.status === 'confirmed');
  
  if (confirmedReservations.length === 0) return null;

  return (
    <div className="mt-12">
      <h2 className="text-2xl font-bold text-[#F5F0E6] mb-6" style={{ fontFamily: 'Cinzel, serif' }}>
        {t('reservations.my_reservations')}
      </h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {confirmedReservations.map(res => (
          <ReservationCard 
            key={res.reservation_id}
            reservation={res}
            onCancel={onCancel}
            t={t}
            lang={lang}
          />
        ))}
      </div>
    </div>
  );
}

function ReservationCard({ reservation: res, onCancel, t, lang }) {
  return (
    <div 
      className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl p-4" 
      data-testid={`table-res-${res.reservation_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-[#F5F0E6] font-semibold text-sm">
            {res.table_name || `${t('reservations.table')} #${res.table_number}`}
          </h4>
          <p className="text-xs text-[#C9A962]">
            {res.table_capacity} {t('reservationsShared.people')}
          </p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
          res.status === 'confirmed' ? 'status-available' : 'status-booked'
        }`}>
          {res.status === 'confirmed' ? t('reservations.confirmed') : t('reservations.cancelled')}
        </span>
      </div>
      
      <div className="space-y-1 text-xs text-[#A8B5AE]">
        <div className="flex items-center gap-1">
          <Calendar size={11} />{res.date}
        </div>
        <div className="flex items-center gap-1">
          <Clock size={11} />{res.time_slot} ({res.duration_hours}h)
        </div>
        <div className="flex items-center gap-1">
          <Users size={11} />{res.party_size} {t('reservationsShared.people')}
        </div>
      </div>
      
      {res.linked_game_reservation && (
        <p className="mt-2 text-xs text-[#C9A962]">
          {t('reservationsShared.linked_game_reservation')}
        </p>
      )}
      
      {res.status === 'confirmed' && !res.linked_game_reservation && (
        <button 
          onClick={() => onCancel(res.reservation_id)}
          className="mt-3 w-full text-xs text-[#C45B4A] bg-[#C45B4A]/10 hover:bg-[#C45B4A]/20 rounded-lg py-1.5 transition-colors"
          data-testid={`cancel-table-${res.reservation_id}`}
        >
          {t('reservations.cancel')}
        </button>
      )}
    </div>
  );
}
